import React from 'react';
import { Body, Button, Flex, Loader, Title, Tooltip } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { InfoMessage } from 'components/InfoMessage/InfoMessage';
import { useUrl } from 'hooks/useUrls';
import { useStatefulHistory } from 'hooks/history';
import { useConsistencyReport } from './hooks/useConsistencyReport';
import { ConsistencyReportView } from './components/ConsistencyReportView';
import { useDownloadConsistencyReport } from './components/useDownloadConsistencyReport';
import { useExportReportToDM } from './components/useExportReportToDM';

export const ViewConsistencyReportPage = () => {
  const consistencyReportId = useUrl.ConsistencyReportId();
  const digitalTwinId = useUrl.DigitalTwinId();
  const history = useStatefulHistory();

  const { report, loading, error } = useConsistencyReport(
    consistencyReportId,
    digitalTwinId
  );

  const {
    handleDownload,
    isDownloadDisabled,
    downloadTooltip,
    downloading,
    error: downloadError,
  } = useDownloadConsistencyReport(report ?? undefined);
  const { exportReportToDM, exportingReport } = useExportReportToDM();

  if (loading) {
    return (
      <StyledPage
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Loader />
      </StyledPage>
    );
  }

  if (error || !report) {
    return (
      <StyledPage direction="column">
        <InfoMessage type="Search" title="Report not found">
          <span>
            {error
              ? `Error loading report: ${error.message}`
              : `No consistency report with the id ${consistencyReportId} was found.`}
          </span>
        </InfoMessage>
      </StyledPage>
    );
  }

  return (
    <StyledPage direction="column">
      <StyledHeader
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Flex direction="row" alignItems="center" gap={10}>
          <Button
            icon="ArrowLeft"
            iconPlacement="left"
            onClick={() => history.goBack()}
            type="ghost"
            aria-label="Go back"
          />
          <Title level={3}>Consistency Report</Title>
          <ReportName level={4}>{report.collectionName}</ReportName>
        </Flex>

        <ActionButtons direction="row" gap={8}>
          <Tooltip content={downloadTooltip}>
            <Button
              icon="Download"
              loading={downloading}
              disabled={isDownloadDisabled}
              onClick={handleDownload}
            >
              Download result
            </Button>
          </Tooltip>
          <Tooltip
            content={
              report.status === 'IN_PROGRESS'
                ? 'Export unavailable while run is in progress.'
                : report.totalFailed === 0
                  ? 'No failures to export.'
                  : 'Export failed instances to Data Model.'
            }
          >
            <Button
              icon="Upload"
              loading={exportingReport}
              disabled={
                exportingReport ||
                report.status === 'IN_PROGRESS' ||
                report.totalFailed === 0
              }
              onClick={() =>
                exportReportToDM({
                  exportType: 'data-consistency',
                  runIds: report.ruleResults
                    .filter((r) => r.totalFailed > 0)
                    .map((r) => r.runStatusId),
                })
              }
            >
              Export report
            </Button>
          </Tooltip>
        </ActionButtons>
      </StyledHeader>

      {downloadError && (
        <DownloadWarning size="small">{downloadError}</DownloadWarning>
      )}

      <ConsistencyReportView report={report} />
    </StyledPage>
  );
};

const StyledPage = styled(Flex)`
  flex: 1;
  overflow: hidden;
  background: var(--cogs-white);
`;

const StyledHeader = styled(Flex)`
  padding: 1rem 2rem 1rem 1rem;
  border-bottom: 1px solid var(--cogs-border-default);
`;

const ActionButtons = styled(Flex)`
  flex-wrap: wrap;
`;

const ReportName = styled(Title)`
  padding-left: 1rem;
  border-left: 1px solid var(--cogs-border-default);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DownloadWarning = styled(Body)`
  padding: 0.5rem 2rem;
  color: var(--cogs-yellow-dark, #956d00);
  background: var(--cogs-yellow-light, #fff8e1);
  border-bottom: 1px solid var(--cogs-border-default);
`;
